import { setLoaded, setLoading, displayLoader, resetLoading } from './actions';
import dialogTypes from '../dialog/types';

// Pending displayLoader timers keyed by per-dispatch requestId (set by
// clientMiddleware on action.meta.requestId). Keying by requestId rather than
// REQUEST type prevents concurrent same-type requests from sharing a timer —
// each dispatch gets its own timer so a fast completion of request A cannot
// accidentally cancel the pending timer for still-in-flight request B.
const pendingLoaderTimers = new Map();

const clearTimerFor = requestId => {
  const id = pendingLoaderTimers.get(requestId);
  if (id !== undefined) {
    clearTimeout(id);
    pendingLoaderTimers.delete(requestId);
  }
};

const clearAllTimers = () => {
  pendingLoaderTimers.forEach(clearTimeout);
  pendingLoaderTimers.clear();
};

export default ({ dispatch }) => next => action => {
  const { types } = action;

  // All promise-based actions dispatched by clientMiddleware carry a `types`
  // array of [REQUEST, SUCCESS, FAILURE] alongside the resolved `type`, and a
  // unique `meta.requestId` that is stable across REQUEST / SUCCESS / FAILURE
  // for the same dispatch.
  if (Array.isArray(types) && types.length === 3) {
    const [REQUEST, SUCCESS, FAILURE] = types;
    const requestId = action.meta?.requestId;

    if (action.type === REQUEST) {
      dispatch(setLoading());
      // Only display the loader if the operation is still in progress after 2s
      // (avoids a flash for fast responses).
      if (requestId) {
        pendingLoaderTimers.set(requestId, setTimeout(() => {
          pendingLoaderTimers.delete(requestId);
          dispatch(displayLoader());
        }, 2000));
      }
    }
    else if (action.type === SUCCESS || action.type === FAILURE) {
      if (requestId) {
        clearTimerFor(requestId);
      }
      dispatch(setLoaded());
    }
  }

  // When a dialog is displayed, cancel all pending timers and fully reset the
  // loader so it doesn't appear over the dialog.
  if (action.type === dialogTypes.DISPLAY) {
    clearAllTimers();
    dispatch(resetLoading());
  }

  next(action);
};
